import { Component } from '@theme/component';

/**
 * Opens the faithful catalogue in a modal booklet with desktop facing pages.
 * Native PDF links stay accessible when enhancement is unavailable.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} bookDialog
 * @property {HTMLElement} bookStage
 * @property {HTMLImageElement} pageImage
 * @property {HTMLImageElement} rightPageImage
 * @property {HTMLSelectElement} pageSelect
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} currentPage
 * @property {HTMLElement} status
 * @property {HTMLElement} readerViewport
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLButtonElement} [zoomButton]
 * @property {HTMLElement} [zoomLabel]
 * @property {HTMLButtonElement} [shareButton]
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLDetailsElement} [shareFallback]
 * @property {HTMLInputElement} [shareLink]
 *
 * @extends Component<Refs>
 */
export class CatalogueReader extends Component {
  requiredRefs = ['bookDialog', 'bookStage', 'pageImage', 'rightPageImage', 'pageSelect',
    'previous', 'next', 'currentPage', 'status', 'readerViewport'];

  #page = 0;
  #pageCount = 0;
  #pageTemplate = '';
  #desktop = false;
  #bookOpen = false;
  #autoOpen = false;
  #zoomed = false;
  #sharing = false;
  #requestGeneration = 0;
  #shareGeneration = 0;
  #displayedKey = '';
  #pendingKey = '';
  #bindings = new AbortController();
  #imageRequest = new AbortController();
  /** @type {HTMLImageElement | undefined} */
  #loadedLeft;
  /** @type {HTMLImageElement | undefined} */
  #loadedRight;
  /** @type {HTMLImageElement | undefined} */
  #requestLeft;
  /** @type {HTMLImageElement | undefined} */
  #requestRight;
  /** @type {HTMLElement | undefined} */
  #returnFocus;
  /** @type {HTMLElement | undefined} */
  #turn;
  /** @type {Animation | undefined} */
  #animation;
  /** @type {MediaQueryList | undefined} */
  #motionQuery;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#bindings.abort();
    this.#cancelRequest();
    this.#cancelTurn();
    this.#shareGeneration += 1;
    this.#bookOpen = false;
    this.#returnFocus = undefined;
    if (this.refs.bookDialog.open) this.refs.bookDialog.close();
  }

  #initialize() {
    this.#bindings.abort();
    this.#bindings = new AbortController();
    this.#cancelTurn();
    const signal = this.#bindings.signal;
    const count = Number(this.dataset.pageCount);
    this.#pageCount = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1;
    this.#pageTemplate = this.dataset.pageTemplate ?? '';
    if (!this.#page) {
      const hashPage = window.location.hash.match(/^#page-(\d+)$/);
      this.#page = hashPage ? Number(hashPage[1]) : 1;
      this.#autoOpen = Boolean(hashPage);
    }
    this.#page = Math.min(this.#pageCount, Math.max(1, this.#page));
    const widthQuery = window.matchMedia('(min-width: 750px)');
    this.#desktop = widthQuery.matches;
    widthQuery.addEventListener('change', (event) => {
      this.#desktop = event.matches;
      this.#showPage(this.#page);
    }, { signal });
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionQuery.addEventListener('change', () => this.#cancelTurn(), { signal });
    this.refs.readerViewport.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.refs.bookDialog.addEventListener('close', this.#finishClose, { signal });
    for (const control of this.querySelectorAll('[data-catalogue-enhancement]')) {
      if (control instanceof HTMLElement) control.hidden = false;
    }
    if (this.refs.shareButton) this.refs.shareButton.hidden = false;
    this.#shareGeneration += 1;
    this.#setSharing(false);
    this.#syncZoom();
    if (this.#bookOpen && !this.refs.bookDialog.open) this.refs.bookDialog.showModal();
    this.#showPage(this.#page);
    this.#syncShareLink();
    if (this.#autoOpen) {
      this.#autoOpen = false;
      this.openBook();
    }
  }

  /** Opens the modal, keeping the PDF fallback when unsupported. @param {MouseEvent} [event] */
  openBook(event) {
    const dialog = this.refs.bookDialog;
    if (typeof dialog.showModal !== 'function') return;
    if (!dialog.open) {
      const target = event?.target;
      const fallback = this.querySelector('[on\\:click="/openBook"]');
      this.#returnFocus = target instanceof HTMLElement ? target :
        fallback instanceof HTMLElement ? fallback :
        document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
      try {
        dialog.showModal();
      } catch {
        return;
      }
    }
    event?.preventDefault();
    this.#bookOpen = true;
    this.classList.add('is-book-open');
    this.#showPage(this.#page);
    this.refs.closeButton?.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} [event] */
  closeBook(event) {
    event?.preventDefault();
    if (this.refs.bookDialog.open) this.refs.bookDialog.close();
    this.#finishClose();
  }

  #finishClose = () => {
    if (this.refs.bookDialog.open) return;
    this.#bookOpen = false;
    this.classList.remove('is-book-open');
    this.#cancelTurn();
    this.#cancelRequest();
    this.#setLoading(false);
    this.#shareGeneration += 1;
    this.#setSharing(false);
    if (this.refs.shareFallback) this.refs.shareFallback.open = false;
    const url = new URL(window.location.href);
    if (/^#page-\d+$/.test(url.hash)) {
      url.hash = '';
      window.history.replaceState(window.history.state, '', url);
    }
    const returnFocus = this.#returnFocus;
    this.#returnFocus = undefined;
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  };

  /** @returns {{left: number, right: number | undefined}} */
  #spread() {
    if (!this.#desktop || this.#page === 1 || this.#page === this.#pageCount) return { left: this.#page, right: undefined };
    const left = this.#page % 2 === 0 ? this.#page : this.#page - 1;
    return { left, right: left < this.#pageCount ? left + 1 : undefined };
  }

  /** @param {MouseEvent} [event] */
  previousPage(event) {
    event?.preventDefault();
    const { left } = this.#spread();
    const previous = this.#desktop ? left === 2 ? 1 : left - 2 : this.#page - 1;
    this.#showPage(previous, true, -1);
  }

  /** @param {MouseEvent} [event] */
  nextPage(event) {
    event?.preventDefault();
    const { left, right } = this.#spread();
    this.#showPage(this.#desktop ? (right ?? left) + 1 : this.#page + 1, true, 1);
  }

  /** @param {Event} event */
  changePage(event) {
    if (!(event.target instanceof HTMLSelectElement) || event.target !== this.refs.pageSelect) return;
    const page = Number(event.target.value);
    this.#showPage(page, true, Math.sign(page - this.#page));
  }

  /** @param {MouseEvent} [event] */
  toggleZoom(event) {
    event?.preventDefault();
    this.#cancelTurn();
    this.#zoomed = !this.#zoomed;
    this.#syncZoom();
  }

  #syncZoom() {
    this.classList.toggle('is-zoomed', this.#zoomed);
    const button = this.refs.zoomButton;
    if (!button) return;
    button.setAttribute('aria-pressed', String(this.#zoomed));
    const label = this.#zoomed ? button.dataset.zoomOut : button.dataset.zoomIn;
    if (label) (this.refs.zoomLabel ?? button).textContent = label;
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('input, select, textarea, button, a, [contenteditable="true"]')) return;
    if (event.key === 'ArrowLeft' && !this.refs.previous.disabled) {
      event.preventDefault();
      this.previousPage();
    } else if (event.key === 'ArrowRight' && !this.refs.next.disabled) {
      event.preventDefault();
      this.nextPage();
    }
  };

  /** @param {number} page @param {boolean} [updateURL] @param {number} [direction] */
  #showPage(page, updateURL = false, direction = 0) {
    if (!Number.isInteger(page) || page < 1 || page > this.#pageCount) return;
    const changed = page !== this.#page;
    this.#page = page;
    const { left, right } = this.#spread();
    const range = right ? `${left}–${right}` : String(left);
    this.refs.pageSelect.value = String(page);
    this.refs.currentPage.textContent = `${range} / ${this.#pageCount}`;
    this.refs.previous.disabled = left === 1;
    this.refs.next.disabled = (right ?? left) === this.#pageCount;
    if (updateURL && changed) {
      const url = new URL(window.location.href);
      url.hash = `page-${page}`;
      window.history.replaceState(window.history.state, '', url);
    }
    this.#syncShareLink();
    this.#cancelTurn();
    if (!this.refs.bookDialog.open) return;
    const leftImage = this.refs.pageImage;
    const rightImage = this.refs.rightPageImage;
    let leftURL;
    let rightURL;
    try {
      leftURL = this.#pageURL(left);
      rightURL = right ? this.#pageURL(right) : undefined;
    } catch {
      this.#cancelRequest();
      this.#showImageError();
      return;
    }
    const key = `${leftURL}|${rightURL ?? ''}`;
    if (this.#pendingKey === key && this.#requestLeft === leftImage && this.#requestRight === rightImage) return;
    if (this.#displayedKey === key && this.#loadedLeft === leftImage && this.#loadedRight === rightImage &&
        leftImage.src === leftURL && leftImage.complete && (!rightURL || rightImage.src === rightURL && rightImage.complete)) {
      this.#cancelRequest();
      this.#setLoading(false);
      this.#showLoadedImages(Boolean(right));
      this.#announce(this.dataset.readerReady, range);
      return;
    }
    this.#cancelRequest();
    this.#imageRequest = new AbortController();
    const signal = this.#imageRequest.signal;
    const generation = ++this.#requestGeneration;
    this.#pendingKey = key;
    this.#requestLeft = leftImage;
    this.#requestRight = rightImage;
    this.#setLoading(true);
    this.#announce(this.dataset.readerLoading, range);
    const current = () => this.isConnected && this.refs.bookDialog.open && !signal.aborted &&
      generation === this.#requestGeneration && leftImage === this.refs.pageImage && rightImage === this.refs.rightPageImage;
    Promise.all([this.#loadImage(leftURL, signal), rightURL ? this.#loadImage(rightURL, signal) : Promise.resolve()])
      .then(() => {
        if (!current()) return;
        const turn = direction ? this.#createTurn(direction, leftURL, rightURL) : undefined;
        leftImage.removeAttribute('srcset');
        leftImage.src = leftURL;
        if (this.dataset.pageAlt) leftImage.alt = this.#interpolate(this.dataset.pageAlt, String(left));
        if (rightURL) {
          rightImage.src = rightURL;
          if (this.dataset.pageAlt) rightImage.alt = this.#interpolate(this.dataset.pageAlt, String(right));
        } else {
          rightImage.removeAttribute('src');
          rightImage.alt = '';
        }
        this.#displayedKey = key;
        this.#loadedLeft = leftImage;
        this.#loadedRight = rightImage;
        this.#pendingKey = '';
        this.#setLoading(false);
        this.#showLoadedImages(Boolean(right));
        this.#announce(this.dataset.readerReady, range);
        this.#imageRequest.abort();
        if (turn) this.#animateTurn(turn, direction);
      })
      .catch((error) => {
        if (!current() || error instanceof DOMException && error.name === 'AbortError') return;
        this.#pendingKey = '';
        this.#showImageError();
        this.#imageRequest.abort();
      });
  }

  /** @param {number} page */
  #pageURL(page) {
    if (!this.#pageTemplate.includes('PAGE_NUMBER')) throw new Error('Missing page URL template');
    const url = new URL(this.#pageTemplate.replace('PAGE_NUMBER', String(page).padStart(2, '0')), document.baseURI);
    if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Unsupported page URL');
    return url.href;
  }

  /** @param {string} url @param {AbortSignal} signal */
  #loadImage(url, signal) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      const abort = () => {
        image.removeAttribute('src');
        reject(new DOMException('Cancelled', 'AbortError'));
      };
      const loaded = () => {
        signal.removeEventListener('abort', abort);
        resolve(image);
      };
      signal.addEventListener('abort', abort, { once: true });
      image.addEventListener('load', loaded, { once: true, signal });
      image.addEventListener('error', () => {
        signal.removeEventListener('abort', abort);
        reject(new Error('Catalogue image could not load'));
      }, { once: true, signal });
      if (signal.aborted) return abort();
      image.src = url;
      if (image.complete && image.naturalWidth > 0) loaded();
    });
  }

  #cancelRequest() {
    this.#imageRequest.abort();
    this.#requestGeneration += 1;
    this.#pendingKey = '';
  }

  /** @param {number} direction @param {string} leftURL @param {string | undefined} rightURL */
  #createTurn(direction, leftURL, rightURL) {
    if (this.#motionQuery?.matches || this.#zoomed || !this.#displayedKey || this.classList.contains('is-error')) return;
    const source = direction > 0 && !this.refs.rightPageImage.hidden ? this.refs.rightPageImage : this.refs.pageImage;
    if (source.hidden || !source.complete || !source.naturalWidth) return;
    const stage = this.refs.bookStage;
    const rect = source.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const turn = document.createElement('div');
    turn.className = 'catalogue-book__turning-page';
    turn.setAttribute('aria-hidden', 'true');
    turn.style.left = `${rect.left - stageRect.left + stage.scrollLeft}px`;
    turn.style.top = `${rect.top - stageRect.top + stage.scrollTop}px`;
    turn.style.width = `${rect.width}px`;
    turn.style.height = `${rect.height}px`;
    turn.style.transformOrigin = direction > 0 ? 'left center' : 'right center';
    const front = /** @type {HTMLImageElement} */ (source.cloneNode(true));
    front.removeAttribute('ref');
    front.removeAttribute('id');
    front.removeAttribute('aria-hidden');
    front.className = 'catalogue-book__turn-face';
    front.alt = '';
    const back = /** @type {HTMLImageElement} */ (front.cloneNode(true));
    back.className = 'catalogue-book__turn-face catalogue-book__turn-face--back';
    back.src = direction > 0 ? leftURL : rightURL ?? leftURL;
    turn.append(front, back);
    stage.append(turn);
    this.#turn = turn;
    return turn;
  }

  /** @param {HTMLElement} turn @param {number} direction */
  #animateTurn(turn, direction) {
    if (typeof turn.animate !== 'function') {
      this.#cancelTurn();
      return;
    }
    // A cover changes the spread's width; align its flap with the new fold.
    const leaf = direction > 0 && !this.refs.rightPageImage.hidden ? this.refs.rightPageImage : this.refs.pageImage;
    const rect = leaf.getBoundingClientRect();
    const stage = this.refs.bookStage;
    const stageRect = stage.getBoundingClientRect();
    turn.style.left = `${rect.left - stageRect.left + stage.scrollLeft}px`;
    turn.style.top = `${rect.top - stageRect.top + stage.scrollTop}px`;
    turn.style.width = `${rect.width}px`;
    turn.style.height = `${rect.height}px`;
    this.classList.add('is-turning');
    const animation = turn.animate([
      { transform: 'rotateY(0deg)' },
      { transform: `rotateY(${direction > 0 ? -165 : 165}deg)` },
    ], { duration: 560, easing: 'cubic-bezier(0.22, 0.65, 0.24, 1)', fill: 'forwards' });
    this.#animation = animation;
    animation.finished.then(() => {
      if (this.#animation === animation) this.#cancelTurn();
    }).catch(() => {});
  }

  #cancelTurn() {
    this.#animation?.cancel();
    this.#animation = undefined;
    this.#turn?.remove();
    this.#turn = undefined;
    this.classList.remove('is-turning');
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    this.refs.readerViewport.setAttribute('aria-busy', String(loading));
  }

  #showImageError() {
    this.#setLoading(false);
    this.classList.add('is-error');
    for (const image of [this.refs.pageImage, this.refs.rightPageImage]) {
      image.hidden = true;
      image.setAttribute('aria-hidden', 'true');
    }
    this.#announce(this.dataset.readerError);
  }

  /** @param {boolean} spread */
  #showLoadedImages(spread) {
    this.classList.remove('is-error');
    this.classList.toggle('is-spread', spread);
    this.refs.pageImage.hidden = false;
    this.refs.pageImage.removeAttribute('aria-hidden');
    this.refs.rightPageImage.hidden = !spread;
    if (spread) this.refs.rightPageImage.removeAttribute('aria-hidden');
    else this.refs.rightPageImage.setAttribute('aria-hidden', 'true');
  }

  /** @param {string | undefined} message @param {string} [page] */
  #announce(message, page) {
    this.refs.status.textContent = this.#interpolate(message ?? '', page);
  }

  /** @param {string} message @param {string} [page] */
  #interpolate(message, page = String(this.#page)) {
    return message.replaceAll('{page}', page).replaceAll('{count}', String(this.#pageCount));
  }

  #shareURL() {
    const url = new URL(window.location.href);
    url.hash = `page-${this.#page || 1}`;
    return url.href;
  }

  #syncShareLink() {
    if (this.refs.shareLink) this.refs.shareLink.value = this.#shareURL();
  }

  /** @param {MouseEvent} [event] */
  openShareLinks(event) {
    event?.preventDefault();
    this.#syncShareLink();
    if (this.refs.shareFallback) this.refs.shareFallback.open = true;
    this.refs.shareLink?.focus();
    this.refs.shareLink?.select();
  }

  /** @param {MouseEvent} event */
  async shareCatalogue(event) {
    event.preventDefault();
    if (this.#sharing) return;
    this.#setSharing(true);
    const generation = ++this.#shareGeneration;
    const url = this.#shareURL();
    const active = () => this.isConnected && generation === this.#shareGeneration;
    try {
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: this.dataset.title ?? document.title, url });
          return;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          if (!active()) return;
        }
      }
      if (active()) await this.#copyLink(url, active);
    } finally {
      if (active()) this.#setSharing(false);
    }
  }

  /** @param {MouseEvent} event */
  async copyCatalogueLink(event) {
    event.preventDefault();
    if (this.#sharing) return;
    this.#setSharing(true);
    const generation = ++this.#shareGeneration;
    const active = () => this.isConnected && generation === this.#shareGeneration;
    try {
      await this.#copyLink(this.#shareURL(), active);
    } finally {
      if (active()) this.#setSharing(false);
    }
  }

  /** @param {boolean} busy */
  #setSharing(busy) {
    this.#sharing = busy;
    if (this.refs.shareButton) this.refs.shareButton.disabled = busy;
    if (this.refs.copyButton) this.refs.copyButton.disabled = busy;
  }

  /** @param {string} url @param {() => boolean} active */
  async #copyLink(url, active) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      if (active()) this.#announce(this.dataset.shareSuccess);
    } catch {
      if (!active()) return;
      if (this.refs.shareFallback) this.refs.shareFallback.open = true;
      const input = this.refs.shareLink;
      if (input) {
        input.value = url;
        input.focus();
        input.select();
      }
      this.#announce(this.dataset.shareError);
    }
  }
}

if (!customElements.get('catalogue-reader')) customElements.define('catalogue-reader', CatalogueReader);
